// WHY: Shared between frontend and backend, so the progress bar, the submit button and the API
// all use the same required fields.
import { hasMultipleOrSeriesEvent } from '../event/series-event-helper'

export type RequiredFieldScope = 'progress' | 'submit'

export type RequiredFieldEntity = 'format' | 'company' | 'institutions' | 'events' | 'jobPostings'

export interface RequiredFieldsContext {
    events?: { type?: string }[]
}

export interface RequiredFieldDefinition {
    fieldName: string
    scopes: RequiredFieldScope[]
    condition?: (context: RequiredFieldsContext) => boolean
}

export type RequiredFieldsConfig = Record<RequiredFieldEntity, RequiredFieldDefinition[]>

export const REQUIRED_FIELDS: RequiredFieldsConfig = {
    // This configuration adjust the defined fields for the progress bar and the required fields for the submit button
    // all fields with the scope "progress" are required for the progress bar and all fields with the
    // scope "submit" are required for the submit button
    // TODO: We start with a minimal set of required fields for the format
    format: [
        { fieldName: 'title', scopes: ['progress', 'submit'] },
        { fieldName: 'hookLine', scopes: ['progress', 'submit'] },
        { fieldName: 'description', scopes: ['progress', 'submit'] },
        { fieldName: 'governanceLevel', scopes: ['progress'] },
        { fieldName: 'image', scopes: ['progress', 'submit'] },
        { fieldName: 'imageAltText', scopes: ['progress', 'submit'] },
        { fieldName: 'topicTags', scopes: ['progress', 'submit'] },
        { fieldName: 'maximumParticipants', scopes: ['progress'] },
    ],
    company: [
        { fieldName: 'name', scopes: ['progress', 'submit'] },
        { fieldName: 'logo', scopes: ['progress'] },
        { fieldName: 'url', scopes: ['progress'] },
    ],
    // Actual completeness (own function set + at least one organizing institution) is computed via
    // hasOrganizingInstitution() in useChapterProgress/formatSubmitValidation, not by field presence.
    institutions: [{ fieldName: 'ownInstitutionFunction', scopes: ['progress', 'submit'] }],
    events: [
        { fieldName: 'type', scopes: ['progress', 'submit'] },
        { fieldName: 'modality', scopes: ['progress'] },
        { fieldName: 'locationMode', scopes: ['progress'] },
        { fieldName: 'title', scopes: ['progress', 'submit'] },
        { fieldName: 'startDate', scopes: ['progress', 'submit'] },
        { fieldName: 'endDate', scopes: ['progress', 'submit'] },
        { fieldName: 'geographicScope', scopes: ['progress'] },
        { fieldName: 'state', scopes: ['progress', 'submit'] },
        { fieldName: 'address', scopes: ['progress', 'submit'] },
        { fieldName: 'postCode', scopes: ['progress', 'submit'] },
        { fieldName: 'city', scopes: ['progress', 'submit'] },
    ],
    // Job postings are tracked for chapter progress but are not part of format submit blocking validation.
    // Because all formats already have a common role for the participant to submit an application without a specific role
    jobPostings: [
        { fieldName: 'searchProfileType', scopes: ['progress'] },
        { fieldName: 'title', scopes: ['progress'] },
        { fieldName: 'description', scopes: ['progress'] },
        {
            fieldName: 'timeCommitment',
            scopes: ['progress'],
            condition: (context: RequiredFieldsContext): boolean =>
                hasMultipleOrSeriesEvent(context.events),
        },
        { fieldName: 'descriptionRoleActivities', scopes: ['progress'] },
        { fieldName: 'profileRequirements', scopes: ['progress'] },
        { fieldName: 'workExperienceType', scopes: ['progress'] },
    ],
}

export const getRequiredFieldNames = (
    entity: RequiredFieldEntity,
    scope: RequiredFieldScope,
    context: RequiredFieldsContext
): string[] => {
    const defs = REQUIRED_FIELDS[entity] ?? []
    return defs
        .filter(
            (field) =>
                field.scopes.includes(scope) &&
                (field.condition === undefined || field.condition(context))
        )
        .map((field) => field.fieldName)
}
